import React, { useEffect, useState, useContext } from "react";
import {
  getPaymentMethods,
  getWalletBalance,
  getFamilyMembersByUserId,
  getCartByUserId,
  getUserAddressByUserId,
  placeOrder,
  deleteCart,
} from "../../services/Cart/PaymentMethod";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { format } from "date-fns";
import { SlotContext } from "../../context/SlotContext";
import { useDispatch } from "react-redux";
import { setCart } from "../../Redux/CartSlice";
import { HiUserCircle } from "react-icons/hi";

export default function PaymentMethod({
  totalAmount,
  totalDiscount = 0,
  cartItems = [],
  userId,
  appliedCouponCode = "",
  appliedCouponId = 0,
  appliedCouponCategoryId = 0,
}) {
  const [methods, setMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [walletBalance, setWalletBalance] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);

  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showMembers, setShowMembers] = useState(false);

  const { selectedSlot } = useContext(SlotContext);
  const dispatch = useDispatch();

  // ✅ Dynamic userId – props se, nahi toh localStorage se
  const finalUserId =
    userId ||
    JSON.parse(localStorage.getItem("medarphaUser") || "{}")?.userId;

  console.log("═══════════════════════════════════════════════");
  console.log("👤 PAYMENT METHOD - USER ID:", finalUserId);
  console.log("═══════════════════════════════════════════════");

  const payableAmount =
    Number(totalAmount || 0) - Number(totalDiscount || 0);

  const requiresSlot = cartItems.some(
    (item) =>
      Number(item.categoryId) === 2 ||
      Number(item.categoryId) === 3
  );

  // Fetch payment methods
  useEffect(() => {
    const fetchMethods = async () => {
      try {
        const res = await getPaymentMethods();
        setMethods(res || []);
        console.log("✅ Payment Methods Loaded:", res);
      } catch (err) {
        console.error("❌ Error fetching payment methods:", err);
        setError("Failed to load payment methods");
      } finally {
        setLoading(false);
      }
    };
    fetchMethods();
  }, []);

  // Fetch wallet balance
  useEffect(() => {
    const fetchBalance = async () => {
      const methodName =
        selectedMethod?.paymentName ||
        selectedMethod?.paymenName;

      if (methodName === "Wallet") {
        try {
          const balance = await getWalletBalance(finalUserId);
          const safeBalance =
            balance?.currentBalance ??
            balance?.balance ??
            balance ??
            0;
          setWalletBalance(Number(safeBalance));
          console.log("💰 Wallet Balance:", safeBalance);
        } catch (err) {
          console.error("❌ Error fetching wallet balance:", err);
          setWalletBalance(0);
        }
      }
    };
    fetchBalance();
  }, [selectedMethod, finalUserId]);

  // Fetch family members
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        if (!finalUserId) return;

        const data = await getFamilyMembersByUserId(finalUserId);
        setFamilyMembers(data || []);
        console.log("👨‍👩‍👧‍👦 Family Members:", data);

        const defaultMember = data.find((m) => m.isDefault);
        setSelectedMember(defaultMember || data[0]);
      } catch (err) {
        console.log("❌ Family error:", err);
      }
    };
    fetchMembers();
  }, [finalUserId]);

  /* ================= PLACE ORDER ================= */
  /* ================= PLACE ORDER ================= */
  const handlePlaceOrder = async () => {
    console.log("🛒 PLACE ORDER STARTED");

    if (!finalUserId) {
      return toast.error("Please login to place order");
    }

    const methodName =
      selectedMethod?.paymentName ||
      selectedMethod?.paymenName;

    // ✅ Validation 1: Payment Method
    if (!selectedMethod) {
      console.error("❌ No payment method selected");
      return toast.error("Please select a payment method");
    }

    // ✅ Validation 2: Wallet Balance
    if (methodName === "Wallet" && walletBalance < payableAmount) {
      console.error("❌ Insufficient wallet balance:", walletBalance, "Need:", payableAmount);
      return toast.error("Insufficient wallet balance");
    }

    setIsProcessing(true);

    try {
      // ✅ Get Cart
      console.log("🛒 Fetching cart for user:", finalUserId);
      const cartRes = await getCartByUserId(finalUserId);
      const cartData = cartRes?.data?.[0] || cartRes?.[0] || cartRes;

      if (!cartData) {
        console.error("❌ Cart is empty or invalid");
        setIsProcessing(false);
        return toast.error("Cart is empty");
      }

      const cartId = cartData.cartId || cartData.id || 0;
      console.log("🛒 Cart ID:", cartId);

      // ✅ Get Address with reliable fallback
      console.log("📍 Fetching address for user:", finalUserId);
      let selectedAddressId = 0;
      try {
        const addressRes = await getUserAddressByUserId(finalUserId);
        const addresses = Array.isArray(addressRes) ? addressRes : addressRes?.data || [];
        const defaultAddr = addresses.find((a) => a.isDefault) || addresses[0];
        if (defaultAddr?.id) {
          selectedAddressId = defaultAddr.id;
        }
      } catch (addrErr) {
        console.warn("⚠️ Address fetch warning:", addrErr);
      }

      // If user has no saved address, fallback to 1 or default address id
      if (!selectedAddressId) {
        selectedAddressId = 1;
      }

      console.log("📍 Final User Address ID:", selectedAddressId);

      // ✅ Build Final Payload matching Flutter App
      const now = new Date();
      const formattedDate = selectedSlot?.date
        ? new Date(selectedSlot.date).toISOString()
        : now.toISOString();

      const slotTimeString = selectedSlot?.time
        ? selectedSlot.time
        : `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;

      const payload = {
        userId: Number(finalUserId),
        cartId: Number(cartId),
        orderNumber: "", // Flutter app sends empty string initially
        orderStatus: 1,  // Status 1 (Pending/Active)
        paymentStatus: 1,
        paymentMethod: Number(selectedMethod?.id || 1),
        subTotal: Number(Number(totalAmount) + Number(totalDiscount)),
        discountAmount: Number(totalDiscount || 0),
        userAddressId: Number(selectedAddressId),
        finalAmount: Number(payableAmount),
        orderDate: formattedDate,
        orderTime: slotTimeString,
        couponId: Number(appliedCouponId || 0),
        couponCategoryId: Number(appliedCouponCategoryId || 0),
      };

      console.log("═══════════════════════════════════════════════");
      console.log("📦 FINAL PAYLOAD BEING SENT:");
      console.log(JSON.stringify(payload, null, 2));
      console.log("═══════════════════════════════════════════════");

      // ✅ Place order
      const orderResponse = await placeOrder(payload);
      console.log("✅ Order Placed Response:", orderResponse);

      toast.success("Order placed successfully!");

      // ✅ Clear local and server cart
      try {
        dispatch(setCart([]));
        if (cartId) {
          await deleteCart(cartId);
        }
      } catch (clearErr) {
        console.warn("Cart cleanup error:", clearErr);
      }

      // ✅ Modern Success Modal with "View My Orders" & "Continue Shopping"
      const oldPopup = document.getElementById("order-success-modal");
      if (oldPopup) oldPopup.remove();

      const popup = document.createElement("div");
      popup.id = "order-success-modal";
      popup.innerHTML = `
        <div style="
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.65);
          display: flex;
          justify-content: center;
          align-items: center;
          z-index: 999999;
          backdrop-filter: blur(6px);
          padding: 16px;
        ">
          <div style="
            width: 100%;
            max-width: 420px;
            background: #ffffff;
            border-radius: 24px;
            padding: 32px 24px;
            text-align: center;
            box-shadow: 0 20px 50px rgba(0,0,0,0.25);
            animation: popupScale 0.3s cubic-bezier(0.16, 1, 0.3, 1);
            position: relative;
          ">
            <div style="
              width: 72px;
              height: 72px;
              background: #dcfce7;
              color: #16a34a;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              margin: 0 auto 16px;
              font-size: 38px;
            ">✓</div>
            <h2 style="margin: 0; font-size: 22px; font-weight: 800; color: #0f172a;">Order Placed!</h2>
            <p style="margin-top: 10px; font-size: 14px; line-height: 1.5; color: #64748b;">
              Your test booking has been confirmed. Our team will arrive at your selected slot.
            </p>
            <div style="margin-top: 24px; display: flex; flex-direction: column; gap: 10px;">
              <button id="view-orders-btn" style="
                background: linear-gradient(135deg, #dc2626, #e11d48);
                color: #ffffff;
                border: none;
                padding: 13px 20px;
                border-radius: 14px;
                font-size: 14px;
                font-weight: 700;
                cursor: pointer;
                box-shadow: 0 4px 12px rgba(220,38,38,0.25);
              ">View My Orders</button>
              <button id="continue-shopping-btn" style="
                background: #f1f5f9;
                color: #475569;
                border: none;
                padding: 12px 20px;
                border-radius: 14px;
                font-size: 13px;
                font-weight: 600;
                cursor: pointer;
              ">Continue Browsing</button>
            </div>
          </div>
        </div>
        <style>
          @keyframes popupScale {
            from { transform: scale(0.85); opacity: 0; }
            to { transform: scale(1); opacity: 1; }
          }
        </style>
      `;

      document.body.appendChild(popup);

      document.getElementById("view-orders-btn").onclick = () => {
        popup.remove();
        window.location.href = "/profile?tab=orders";
      };

      document.getElementById("continue-shopping-btn").onclick = () => {
        popup.remove();
        window.location.href = "/";
      };

    } catch (err) {
      console.error("═══════════════════════════════════════════════");
      console.error("❌ ORDER FAILED:", err);
      console.error("═══════════════════════════════════════════════");
      
      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.title ||
        err?.message ||
        "Order failed";
      
      toast.error(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  const totalMRP = cartItems.reduce(
    (sum, item) => sum + (item.originalPrice ? Number(item.originalPrice) * (item.quantity || 1) : Number(item.price) * (item.quantity || 1)),
    0
  );
  const testDiscountSavings = totalMRP > Number(totalAmount) ? totalMRP - Number(totalAmount) : 0;
  const couponSavings = Number(totalDiscount || 0);
  const totalCombinedSavings = testDiscountSavings + couponSavings;

  return (
    <div className="bg-white border border-slate-200/90 p-3 sm:p-3.5 rounded-2xl shadow-sm space-y-2.5 font-sans" onClick={(e) => e.stopPropagation()}>
      <ToastContainer />

      {/* Patient / Family Member Selection */}
      {selectedMember && (
        <div className="border border-red-100 bg-red-50/40 rounded-xl p-2.5">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-r from-red-600 to-rose-600 text-white flex items-center justify-center text-xs font-bold shadow-xs">
                {selectedMember.firstName ? selectedMember.firstName.charAt(0).toUpperCase() : "P"}
              </div>
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="font-extrabold text-xs sm:text-sm text-slate-900 leading-tight">
                    {selectedMember.firstName} {selectedMember.lastName}
                  </p>
                  <span className="text-[9px] font-bold text-red-700 bg-red-100 px-1.5 py-0.5 rounded">
                    {selectedMember.relationName || "Self"}
                  </span>
                </div>
                <p className="text-[11px] text-slate-500 font-medium">Sample collection for this patient</p>
              </div>
            </div>
            <button
              onClick={() => setShowMembers(!showMembers)}
              className="text-red-600 border border-red-200 px-2.5 py-1 rounded-lg text-xs font-bold bg-white hover:bg-red-50 transition-colors shadow-2xs cursor-pointer"
            >
              Switch
            </button>
          </div>

          {showMembers && (
            <div className="mt-2 pt-2 border-t border-red-100 space-y-1 max-h-[120px] overflow-y-auto custom-scrollbar">
              {familyMembers.map((m) => (
                <div
                  key={m.id}
                  onClick={() => {
                    setSelectedMember(m);
                    setShowMembers(false);
                  }}
                  className={`p-1.5 rounded-lg cursor-pointer text-xs font-semibold flex items-center justify-between transition-colors ${
                    selectedMember?.id === m.id
                      ? "bg-red-100/80 text-red-900 border border-red-200"
                      : "hover:bg-white text-slate-700 border border-transparent"
                  }`}
                >
                  <span>{m.firstName} {m.lastName}</span>
                  <span className="text-xs text-slate-500 font-normal">({m.relationName || "Self"})</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Bill Summary Table with Itemized Discounts */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs space-y-1.5">
        <h4 className="font-black text-slate-900 pb-1 border-b border-slate-200 uppercase tracking-wider text-[10px]">
          Bill Breakdown
        </h4>
        
        <div className="flex justify-between text-slate-600 font-medium text-xs">
          <span>Total MRP:</span>
          <span className="font-bold text-slate-800">₹{totalMRP.toFixed(2)}</span>
        </div>

        {testDiscountSavings > 0 && (
          <div className="flex justify-between text-emerald-700 font-bold text-xs">
            <span>Special Test Discount:</span>
            <span>- ₹{testDiscountSavings.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between text-slate-600 font-medium text-xs">
          <span>Home Sample Collection:</span>
          <span className="text-emerald-700 font-extrabold bg-emerald-100 px-1.5 py-0.5 rounded text-[10px]">FREE</span>
        </div>

        {couponSavings > 0 && (
          <div className="flex justify-between text-emerald-700 font-bold text-xs">
            <span>Coupon Discount ({appliedCouponCode}):</span>
            <span>- ₹{couponSavings.toFixed(2)}</span>
          </div>
        )}

        <div className="flex justify-between font-black text-sm text-slate-900 pt-1.5 border-t border-dashed border-slate-300">
          <span>Final Payable:</span>
          <span className="text-sm font-black text-slate-900">₹{payableAmount.toFixed(2)}</span>
        </div>

        {totalCombinedSavings > 0 && (
          <div className="bg-emerald-100/80 border border-emerald-200 text-emerald-900 font-extrabold text-xs py-1 px-2 rounded-lg text-center mt-1">
            🎉 You save a total of ₹{totalCombinedSavings.toFixed(2)} on this order!
          </div>
        )}
      </div>

      {/* Selected Slot Information */}
      {selectedSlot?.time && (
        <div className="border border-slate-200 rounded-xl p-2.5 bg-white text-xs">
          <h5 className="font-bold text-slate-900 mb-1 flex items-center gap-1 text-[10px] uppercase tracking-wider">
            <span>Appointment Time</span>
          </h5>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="bg-slate-100 text-slate-800 border border-slate-200 px-2 py-0.5 text-xs rounded-md font-medium">
              📅 {selectedSlot?.date ? format(new Date(selectedSlot.date), "dd MMM yyyy") : "N/A"}
            </span>
            <span className="bg-red-50 text-red-600 border border-red-200 px-2 py-0.5 text-xs rounded-md font-bold">
              ⏰ {selectedSlot?.time || "N/A"}
            </span>
          </div>
        </div>
      )}

      {/* Payment Methods Dropdown */}
      <div>
        <label className="text-[10px] font-black text-slate-700 uppercase tracking-wider block mb-1">
          Select Payment Method
        </label>
        {!loading && methods.length > 0 ? (
          <select
            className="w-full border border-slate-300 p-2.5 rounded-xl text-xs sm:text-sm font-bold bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 cursor-pointer transition-all shadow-2xs"
            value={selectedMethod?.id || ""}
            onChange={(e) =>
              setSelectedMethod(methods.find((m) => m.id === Number(e.target.value)))
            }
          >
            <option value="">-- Choose Payment Method --</option>
            {methods.map((m) => (
              <option key={m.id} value={m.id}>
                {m.paymentName || m.paymenName}
              </option>
            ))}
          </select>
        ) : (
          <div className="py-1 text-center text-xs text-slate-400">Loading payment options...</div>
        )}
      </div>

      {/* Wallet Balance Info */}
      {(selectedMethod?.paymentName === "Wallet" || selectedMethod?.paymenName === "Wallet") && (
        <div className="bg-amber-50 border border-amber-200 p-2 rounded-xl flex justify-between items-center text-xs">
          <span className="text-amber-800 font-bold">Wallet Balance Available:</span>
          <span className="font-black text-amber-900 text-xs">₹{walletBalance}</span>
        </div>
      )}

      {error && <p className="text-red-600 text-xs font-bold mb-1">⚠️ {error}</p>}

      {/* Confirm & Place Order CTA */}
      <button
        onClick={handlePlaceOrder}
        disabled={isProcessing}
        className="w-full bg-gradient-to-r from-red-600 to-rose-600 hover:from-red-700 hover:to-rose-700 text-white font-extrabold py-3 rounded-xl transition-all disabled:opacity-50 shadow-md hover:shadow-lg flex items-center justify-center gap-2 text-xs sm:text-sm cursor-pointer"
      >
        {isProcessing ? (
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            <span>Securing Order...</span>
          </div>
        ) : (
          <span>Confirm & Pay ₹{payableAmount.toFixed(2)}</span>
        )}
      </button>
    </div>
  );
}